/**
 * object-art — SVG illustrations of the home toy objects.
 *
 * Each registry module has a personality-rich OBJECT (Melodimals creatures
 * and toys with faces), not a text card. Common style: rounded, saturated
 * bodies (joy palette), soft dark rim, cheeks, and an elliptical ground
 * shadow (--toy-* tokens in toy.css). Everything is decorative
 * (aria-hidden): the accessible name lives in the container button.
 */

import type { Component, JSX } from "solid-js";
import { Dynamic } from "solid-js/web";

const RIM = "var(--toy-rim)";
const INK = "var(--toy-ink)";
const WHITE = "var(--toy-white)";
const CHEEK = "var(--toy-cheek)";
const SHADOW = "var(--toy-ground)";
const WOOD = "var(--toy-wood)";

interface ArtProps {
  class?: string;
}

function ArtSvg(props: { class?: string; children: JSX.Element }): JSX.Element {
  return (
    <svg viewBox="0 0 96 96" width="100%" height="100%" class={props.class} aria-hidden="true">
      {props.children}
    </svg>
  );
}

/** Standard face: bright eyes + smile + cheeks. */
function Face(props: { cx: number; cy: number; scale?: number }): JSX.Element {
  return (
    <g transform={`translate(${props.cx} ${props.cy}) scale(${props.scale ?? 1})`}>
      <circle cx="-7" cy="-2" r="2.9" fill={INK} />
      <circle cx="7" cy="-2" r="2.9" fill={INK} />
      <circle cx="-6.1" cy="-2.9" r="1" fill={WHITE} />
      <circle cx="7.9" cy="-2.9" r="1" fill={WHITE} />
      <circle cx="-12" cy="3.5" r="3" fill={CHEEK} />
      <circle cx="12" cy="3.5" r="3" fill={CHEEK} />
      <path
        d="M-5 4 q5 5 10 0"
        fill="none"
        stroke={INK}
        stroke-width="2.2"
        stroke-linecap="round"
      />
    </g>
  );
}

function Ground(): JSX.Element {
  return <ellipse cx="48" cy="88" rx="26" ry="5" fill={SHADOW} />;
}

/** Chubby blue piano with smiling keys. */
export function PianoArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <rect
        x="12"
        y="26"
        width="72"
        height="58"
        rx="16"
        fill="var(--joy-sky)"
        stroke={RIM}
        stroke-width="2"
      />
      <rect x="18" y="31" width="60" height="16" rx="8" fill="oklch(1 0 0 / 0.35)" />
      <Face cx={48} cy={46} />
      <rect
        x="20"
        y="60"
        width="56"
        height="18"
        rx="6"
        fill={WHITE}
        stroke={RIM}
        stroke-width="1.5"
      />
      <path
        d="M29 60 v18 M38 60 v18 M48 60 v18 M58 60 v18 M67 60 v18"
        stroke={RIM}
        stroke-width="1.5"
      />
      <rect x="26" y="60" width="6" height="10" rx="2" fill={INK} />
      <rect x="44" y="60" width="6" height="10" rx="2" fill={INK} />
      <rect x="63" y="60" width="6" height="10" rx="2" fill={INK} />
      <circle cx="22" cy="22" r="1.8" fill="var(--joy-grape)" />
      <path
        d="M74 14 q3 6 -2 9"
        fill="none"
        stroke="var(--joy-grape)"
        stroke-width="2"
        stroke-linecap="round"
      />
      <circle cx="70" cy="24" r="2.4" fill="var(--joy-grape)" />
    </ArtSvg>
  );
}

/** Rainbow xylophone with a face on the frame and a mallet. */
export function XylophoneArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <path d="M14 38 h68 l-8 46 h-52 z" fill="var(--joy-sunshine)" stroke={RIM} stroke-width="2" />
      <rect
        x="22"
        y="44"
        width="52"
        height="7"
        rx="3.5"
        fill="var(--joy-coral)"
        stroke={RIM}
        stroke-width="1.2"
      />
      <rect
        x="25"
        y="54"
        width="46"
        height="7"
        rx="3.5"
        fill="var(--joy-tangerine)"
        stroke={RIM}
        stroke-width="1.2"
      />
      <rect
        x="28"
        y="64"
        width="40"
        height="7"
        rx="3.5"
        fill="var(--joy-grass)"
        stroke={RIM}
        stroke-width="1.2"
      />
      <rect
        x="31"
        y="74"
        width="34"
        height="7"
        rx="3.5"
        fill="var(--joy-grape)"
        stroke={RIM}
        stroke-width="1.2"
      />
      <Face cx={48} cy={30} scale={0.9} />
      <g transform="rotate(28 78 26)">
        <rect x="76" y="10" width="4" height="26" rx="2" fill={WOOD} />
        <circle cx="78" cy="9" r="6" fill="var(--joy-bubblegum)" stroke={RIM} stroke-width="1.5" />
      </g>
    </ArtSvg>
  );
}

/** Red drum with a face and crossed drumsticks. */
export function DrumsArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <g transform="rotate(-24 30 26)">
        <rect x="28" y="6" width="4" height="30" rx="2" fill={WOOD} />
        <circle cx="30" cy="5" r="5" fill="var(--joy-sunshine)" stroke={RIM} stroke-width="1.5" />
      </g>
      <g transform="rotate(24 66 26)">
        <rect x="64" y="6" width="4" height="30" rx="2" fill={WOOD} />
        <circle cx="66" cy="5" r="5" fill="var(--joy-sunshine)" stroke={RIM} stroke-width="1.5" />
      </g>
      <path
        d="M16 42 v26 q0 16 32 16 q32 0 32 -16 v-26"
        fill="var(--joy-coral)"
        stroke={RIM}
        stroke-width="2"
      />
      <path
        d="M16 50 l16 8 l-16 8 M80 50 l-16 8 l16 8 M40 46 l16 6 l-16 6 l16 6"
        fill="none"
        stroke="oklch(1 0 0 / 0.55)"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <ellipse cx="48" cy="42" rx="32" ry="12" fill={WHITE} stroke={RIM} stroke-width="2" />
      <Face cx={48} cy={41} scale={0.85} />
    </ArtSvg>
  );
}

/** Little singing bird over a note — the songs module. */
export function SongsArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <path
        d="M60 66 v-38 q0 -6 8 -7 l10 -2 q4 -1 4 3 v34"
        fill="none"
        stroke="var(--joy-grape)"
        stroke-width="5"
        stroke-linecap="round"
      />
      <ellipse cx="55" cy="68" rx="8" ry="6" fill="var(--joy-grape)" />
      <ellipse cx="77" cy="58" rx="8" ry="6" fill="var(--joy-grape)" />
      <circle cx="34" cy="56" r="22" fill="var(--joy-mint)" stroke={RIM} stroke-width="2" />
      <path
        d="M14 58 q-8 2 -6 10 q6 2 12 -4"
        fill="var(--joy-grass)"
        stroke={RIM}
        stroke-width="1.5"
      />
      <circle cx="34" cy="42" r="14" fill="var(--joy-mint)" stroke={RIM} stroke-width="2" />
      <path d="M46 42 l8 3 l-8 3 z" fill="var(--joy-tangerine)" stroke={RIM} stroke-width="1" />
      <circle cx="30" cy="40" r="2.6" fill={INK} />
      <circle cx="38" cy="40" r="2.6" fill={INK} />
      <circle cx="30.8" cy="39.2" r="0.9" fill={WHITE} />
      <circle cx="38.8" cy="39.2" r="0.9" fill={WHITE} />
      <circle cx="26" cy="46" r="2.6" fill={CHEEK} />
      <circle cx="42" cy="46" r="2.6" fill={CHEEK} />
      <path
        d="M25 24 q2 -6 8 -6 M34 17 q5 -2 8 2"
        fill="none"
        stroke="var(--joy-grass)"
        stroke-width="2.5"
        stroke-linecap="round"
      />
    </ArtSvg>
  );
}

/** Simon's monkey with headphones — repeats the melody. */
export function SimonArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <circle cx="20" cy="46" r="10" fill="var(--joy-tangerine)" stroke={RIM} stroke-width="2" />
      <circle cx="76" cy="46" r="10" fill="var(--joy-tangerine)" stroke={RIM} stroke-width="2" />
      <circle cx="48" cy="52" r="30" fill="var(--joy-tangerine)" stroke={RIM} stroke-width="2" />
      <path
        d="M22 34 q26 -22 52 0"
        fill="none"
        stroke="var(--joy-grape)"
        stroke-width="6"
        stroke-linecap="round"
      />
      <ellipse
        cx="20"
        cy="42"
        rx="7"
        ry="9"
        fill="var(--joy-grape)"
        stroke={RIM}
        stroke-width="1.5"
      />
      <ellipse
        cx="76"
        cy="42"
        rx="7"
        ry="9"
        fill="var(--joy-grape)"
        stroke={RIM}
        stroke-width="1.5"
      />
      <ellipse cx="48" cy="58" rx="19" ry="16" fill="oklch(0.95 0.04 85)" />
      <Face cx={48} cy={54} />
      <circle cx="48" cy="30" r="3" fill="var(--joy-sunshine)" />
      <path
        d="M63 66 q4 4 0 8 M67 62 q7 6 1 14"
        fill="none"
        stroke="var(--joy-grape)"
        stroke-width="2"
        stroke-linecap="round"
      />
    </ArtSvg>
  );
}

/** Fox kit with huge ears listening — who made that sound? */
export function WhoSoundsArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <path
        d="M20 40 l-4 -24 l20 12 z"
        fill="var(--joy-coral)"
        stroke={RIM}
        stroke-width="2"
        stroke-linejoin="round"
      />
      <path
        d="M76 40 l4 -24 l-20 12 z"
        fill="var(--joy-coral)"
        stroke={RIM}
        stroke-width="2"
        stroke-linejoin="round"
      />
      <path d="M22 37 l-1.5 -12 l10 6 z" fill={WHITE} />
      <path d="M74 37 l1.5 -12 l-10 6 z" fill={WHITE} />
      <circle cx="48" cy="56" r="28" fill="var(--joy-coral)" stroke={RIM} stroke-width="2" />
      <ellipse cx="48" cy="66" rx="16" ry="12" fill={WHITE} />
      <Face cx={48} cy={56} />
      <ellipse cx="48" cy="66" rx="3.4" ry="2.6" fill={INK} />
      <path
        d="M8 52 q5 4 0 8 M4 46 q9 8 0 20"
        fill="none"
        stroke="var(--joy-sky)"
        stroke-width="2.6"
        stroke-linecap="round"
      />
      <path
        d="M88 52 q-5 4 0 8 M92 46 q-9 8 0 20"
        fill="none"
        stroke="var(--joy-sky)"
        stroke-width="2.6"
        stroke-linecap="round"
      />
    </ArtSvg>
  );
}

/**
 * Two friendly frogs — the turn-based Duo game.
 *
 * Duo is RETIRED from the product (2026-08-03), so this object is not rendered today: the
 * registry does not offer the module and the dispatcher below never looks for it. It is kept—these
 * are a few dependency-free SVG primitives—for the same reason as the rest of the
 * implementation: reactivating the feature should be configuration, not redrawing.
 * See `docs/P2P_SIGNALING_ENABLEMENT.md`.
 */
export function DuoArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <g>
        <circle cx="32" cy="58" r="22" fill="var(--joy-grass)" stroke={RIM} stroke-width="2" />
        <circle cx="22" cy="38" r="7" fill="var(--joy-grass)" stroke={RIM} stroke-width="2" />
        <circle cx="42" cy="38" r="7" fill="var(--joy-grass)" stroke={RIM} stroke-width="2" />
        <circle cx="22" cy="38" r="3" fill={INK} />
        <circle cx="42" cy="38" r="3" fill={INK} />
        <circle cx="23" cy="37" r="1" fill={WHITE} />
        <circle cx="43" cy="37" r="1" fill={WHITE} />
        <circle cx="22" cy="58" r="3" fill={CHEEK} />
        <path
          d="M24 58 q8 7 16 0"
          fill="none"
          stroke={INK}
          stroke-width="2.2"
          stroke-linecap="round"
        />
      </g>
      <g>
        <circle cx="68" cy="62" r="18" fill="var(--joy-grape)" stroke={RIM} stroke-width="2" />
        <circle cx="60" cy="46" r="6" fill="var(--joy-grape)" stroke={RIM} stroke-width="2" />
        <circle cx="76" cy="46" r="6" fill="var(--joy-grape)" stroke={RIM} stroke-width="2" />
        <circle cx="60" cy="46" r="2.6" fill={INK} />
        <circle cx="76" cy="46" r="2.6" fill={INK} />
        <circle cx="60.8" cy="45.2" r="0.9" fill={WHITE} />
        <circle cx="76.8" cy="45.2" r="0.9" fill={WHITE} />
        <circle cx="78" cy="62" r="2.6" fill={CHEEK} />
        <path
          d="M62 62 q6 6 12 0"
          fill="none"
          stroke={INK}
          stroke-width="2"
          stroke-linecap="round"
        />
      </g>
      <path
        d="M42 20 q6 -8 12 0 M48 16 v10"
        fill="none"
        stroke="var(--joy-coral)"
        stroke-width="2.6"
        stroke-linecap="round"
      />
    </ArtSvg>
  );
}

/** Drumming star with dots for counting. */
export function CountAndPlayArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <path
        d="M48 10 l10.5 21 l23 3.4 l-16.6 16.4 l3.9 23 L48 63 l-20.8 10.8 l3.9 -23 L14.5 34.4 l23 -3.4 z"
        fill="var(--joy-sunshine)"
        stroke={RIM}
        stroke-width="2"
        stroke-linejoin="round"
      />
      <Face cx={48} cy={42} />
      <circle cx="30" cy="80" r="4" fill="var(--joy-coral)" stroke={RIM} stroke-width="1.2" />
      <circle cx="48" cy="84" r="4" fill="var(--joy-sky)" stroke={RIM} stroke-width="1.2" />
      <circle cx="66" cy="80" r="4" fill="var(--joy-grass)" stroke={RIM} stroke-width="1.2" />
    </ArtSvg>
  );
}

/** Sticker album — the prize showcase. */
export function StickerAlbumArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <Ground />
      <rect
        x="16"
        y="18"
        width="64"
        height="64"
        rx="10"
        fill="var(--joy-bubblegum)"
        stroke={RIM}
        stroke-width="2"
        transform="rotate(-4 48 50)"
      />
      <rect x="22" y="24" width="52" height="52" rx="7" fill={WHITE} transform="rotate(-4 48 50)" />
      <g transform="rotate(-4 48 50)">
        <path
          d="M40 34 l3 6 l7 1 l-5 5 l1 7 l-6 -3 l-6 3 l1 -7 l-5 -5 l7 -1 z"
          fill="var(--joy-sunshine)"
          stroke={RIM}
          stroke-width="1.2"
        />
        <circle cx="60" cy="58" r="7" fill="var(--joy-sky)" stroke={RIM} stroke-width="1.2" />
        <path
          d="M32 60 q4 -6 8 0 q-4 6 -8 0"
          fill="var(--joy-coral)"
          stroke={RIM}
          stroke-width="1"
        />
      </g>
    </ArtSvg>
  );
}

/** Small "grown-ups" door: discreet, with a padlock. */
export function ParentsDoorArt(props: ArtProps): JSX.Element {
  return (
    <ArtSvg class={props.class}>
      <path
        d="M24 88 v-44 q0 -22 24 -22 q24 0 24 22 v44 z"
        fill={WOOD}
        stroke={RIM}
        stroke-width="2"
      />
      <path d="M32 88 v-42 q0 -15 16 -15 q16 0 16 15 v42 z" fill="oklch(0.72 0.09 65)" />
      <circle cx="60" cy="56" r="3.4" fill="var(--joy-sunshine)" stroke={RIM} stroke-width="1.2" />
      <rect
        x="40"
        y="40"
        width="16"
        height="12"
        rx="3"
        fill="none"
        stroke={WHITE}
        stroke-width="2.4"
      />
      <path
        d="M43 40 v-3 a5 5 0 0 1 10 0 v3"
        fill="none"
        stroke={WHITE}
        stroke-width="2.4"
        stroke-linecap="round"
      />
    </ArtSvg>
  );
}

const MODULE_ART: Record<string, Component<ArtProps>> = {
  piano: PianoArt,
  xylophone: XylophoneArt,
  drums: DrumsArt,
  songs: SongsArt,
  simon: SimonArt,
  "who-sounds": WhoSoundsArt,
  duo: DuoArt,
  "count-and-play": CountAndPlayArt,
};

/** Dispatcher: module id → its illustrated object (fallback: the star). */
export function ModuleObjectArt(props: { id: string; class?: string }): JSX.Element {
  return <Dynamic component={MODULE_ART[props.id] ?? CountAndPlayArt} class={props.class} />;
}
